import { useState } from 'react';
import { apiKeys, ApiClientError, type CreatedApiKey } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { useAuth } from '../hooks/useAuth';
import { formatDate, formatRelative } from '../lib/format';
import { Alert, Badge, Card, CopyButton, Empty, Modal, Spinner } from '../components/ui';

export function ApiKeys() {
  const { account } = useAuth();
  const keys = useResource(() => apiKeys.list(), []);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Held in state because the plaintext secret is returned exactly once —
  // if this component unmounts before the user copies it, it is gone.
  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);

  const verified = account?.email_verified ?? false;

  async function create() {
    setError(null);
    setSubmitting(true);

    try {
      const key = await apiKeys.create({ name: name || undefined, mode });
      setRevealed(key);
      setCreating(false);
      setName('');
      await keys.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the key');
    } finally {
      setSubmitting(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this key? Requests using it will start failing immediately.')) return;

    try {
      await apiKeys.revoke(id);
      await keys.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not revoke the key');
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>API keys</h1>
        <p>Authenticate requests with a secret key as a bearer token.</p>
      </div>

      {error && <Alert>{error}</Alert>}
      {!verified && (
        <Alert tone="warning">Verify your email address before creating API keys.</Alert>
      )}

      <Card
        title="Your keys"
        actions={
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setCreating(true)}
            disabled={!verified}
          >
            Create key
          </button>
        }
      >
        {keys.loading ? (
          <Spinner />
        ) : !keys.data?.length ? (
          <Empty>No API keys yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mode</th>
                  <th>Secret</th>
                  <th>Last used</th>
                  <th>Requests</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.data.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name ?? <span className="muted">Untitled</span>}</td>
                    <td>
                      <Badge tone={key.mode === 'live' ? 'accent' : 'neutral'}>{key.mode}</Badge>
                    </td>
                    <td className="mono">{key.secret_masked}</td>
                    <td className="muted">{formatRelative(key.last_used_at)}</td>
                    <td className="mono">{key.request_count}</td>
                    <td className="muted">{formatDate(key.created_at)}</td>
                    <td>
                      {key.status === 'revoked' ? (
                        <Badge tone="danger">revoked</Badge>
                      ) : (
                        <button className="btn btn-sm btn-danger" onClick={() => revoke(key.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <Modal
          title="Create API key"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button className="btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={create} disabled={submitting}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </>
          }
        >
          <label className="field">
            <span>Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Production server"
              autoFocus
            />
          </label>

          <label className="field">
            <span>Mode</span>
            <select value={mode} onChange={(event) => setMode(event.target.value as 'test' | 'live')}>
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </label>
        </Modal>
      )}

      {revealed && (
        <Modal
          title="Save your secret key"
          onClose={() => setRevealed(null)}
          footer={
            <button className="btn btn-primary" onClick={() => setRevealed(null)}>
              I've saved it
            </button>
          }
        >
          <Alert tone="warning">
            This is the only time the secret is shown. Store it somewhere safe.
          </Alert>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div className="secret-reveal" style={{ flex: 1 }}>
              {revealed.secret_key}
            </div>
            <CopyButton value={revealed.secret_key} />
          </div>

          <p className="muted" style={{ marginBottom: 0 }}>
            Public key: <code>{revealed.public_key}</code>
          </p>
        </Modal>
      )}
    </>
  );
}
